/**
 * Unified Cache Manager - DRY Implementation
 *
 * Single Responsibility: Manage all application caching
 * Separation of Concerns: Cache logic separated from business logic
 * DRY: Single implementation for all cache needs
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  storage: "memory" | "sessionStorage" | "localStorage";
  maxSize?: number; // Maximum number of entries
  version?: string; // Cache version for invalidation
}

interface LoadingPromiseEntry<T> {
  promise: Promise<T>;
  timestamp: number;
}

class UnifiedCacheManager {
  private static memoryCache = new Map<string, CacheEntry<any>>();
  private static loadingPromises = new Map<string, LoadingPromiseEntry<any>>();
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly PROMISE_TTL = 30 * 1000; // 30 seconds
  private static readonly MAX_MEMORY_SIZE = 100;
  private static cleanupInterval: NodeJS.Timeout | null = null;

  /**
   * Initialize the cache manager with automatic cleanup
   */
  static initialize(): void {
    if (this.cleanupInterval) return;

    // Cleanup expired entries every 60 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredEntries();
      this.cleanupExpiredPromises();
    }, 60000);

    // Cleanup on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.destroy();
      });
    }
  }

  /**
   * Get cached data with automatic expiration checking
   */
  static get<T>(key: string, config: CacheConfig): T | null {
    const entry = this.getEntry<T>(key, config);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > config.ttl;
    const isVersionMismatch =
      config.version && entry.version !== config.version;

    if (isExpired || isVersionMismatch) {
      this.delete(key, config);
      return null;
    }

    return entry.data;
  }

  /**
   * Set cached data with version and size management
   */
  static set<T>(key: string, data: T, config: CacheConfig): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      version: config.version || "1.0.0",
    };

    switch (config.storage) {
      case "memory":
        this.setMemoryEntry(key, entry, config);
        break;
      case "sessionStorage":
        this.setStorageEntry(key, entry, "sessionStorage");
        break;
      case "localStorage":
        this.setStorageEntry(key, entry, "localStorage");
        break;
    }
  }

  /**
   * Load data with request deduplication
   */
  static async load<T>(
    key: string,
    loader: () => Promise<T>,
    config: CacheConfig
  ): Promise<T> {
    // Check cache first
    const cached = this.get<T>(key, config);
    if (cached) {
      return cached;
    }

    // Check for existing loading promise
    const existingPromise = this.loadingPromises.get(key);
    if (
      existingPromise &&
      Date.now() - existingPromise.timestamp < this.PROMISE_TTL
    ) {
      return existingPromise.promise;
    }

    // Create new loading promise

    const loadingPromise = loader();

    this.loadingPromises.set(key, {
      promise: loadingPromise,
      timestamp: Date.now(),
    });

    try {
      const data = await loadingPromise;
      this.set(key, data, config);
      return data;
    } finally {
      this.loadingPromises.delete(key);
    }
  }

  /**
   * Delete cached entry
   */
  static delete(key: string, config: CacheConfig): void {
    switch (config.storage) {
      case "memory":
        this.memoryCache.delete(key);
        break;
      case "sessionStorage":
      case "localStorage":
        this.deleteStorageEntry(key, config.storage);
        break;
    }
  }

  /**
   * Clear all cache entries (useful for logout)
   */
  static clear(pattern?: string): void {
    // Clear memory cache
    if (pattern) {
      const keysToDelete = Array.from(this.memoryCache.keys()).filter(key =>
        key.includes(pattern)
      );
      keysToDelete.forEach(key => this.memoryCache.delete(key));
    } else {
      this.memoryCache.clear();
    }

    // Clear storage caches
    this.clearStorage("sessionStorage", pattern);
    this.clearStorage("localStorage", pattern);

    // Clear loading promises
    if (pattern) {
      const keysToDelete = Array.from(this.loadingPromises.keys()).filter(key =>
        key.includes(pattern)
      );
      keysToDelete.forEach(key => this.loadingPromises.delete(key));
    } else {
      this.loadingPromises.clear();
    }
  }

  /**
   * Get cache statistics for debugging
   */
  static getStats(): {
    memoryEntries: number;
    loadingPromises: number;
    storageEntries: { sessionStorage: number; localStorage: number };
  } {
    return {
      memoryEntries: this.memoryCache.size,
      loadingPromises: this.loadingPromises.size,
      storageEntries: {
        sessionStorage: this.getStorageSize("sessionStorage"),
        localStorage: this.getStorageSize("localStorage"),
      },
    };
  }

  /**
   * Clean up and destroy the cache manager
   */
  static destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.memoryCache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Invalidate cache entries by pattern (enhanced for file operations)
   */
  static invalidate(pattern: string, notify?: boolean): void {
    // Clear memory cache
    if (pattern) {
      const keysToDelete = Array.from(this.memoryCache.keys()).filter(key =>
        key.includes(pattern)
      );
      keysToDelete.forEach(key => this.memoryCache.delete(key));
    } else {
      this.memoryCache.clear();
    }

    // Clear storage caches
    this.clearStorage("sessionStorage", pattern);
    this.clearStorage("localStorage", pattern);

    // Clear loading promises
    if (pattern) {
      const keysToDelete = Array.from(this.loadingPromises.keys()).filter(key =>
        key.includes(pattern)
      );
      keysToDelete.forEach(key => this.loadingPromises.delete(key));
    } else {
      this.loadingPromises.clear();
    }

    // Notify cache invalidation if requested
    if (notify && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cache-invalidated", {
          detail: { pattern },
        })
      );
    }
  }

  /**
   * Invalidate file-related cache entries for a specific agent
   */
  static invalidateFileCache(agentId: string): void {
    const patterns = [
      `files_${agentId}`,
      `corpus_${agentId}`,
      `ingestion_${agentId}`,
      `status_${agentId}`,
    ];

    patterns.forEach(pattern => this.invalidate(pattern, true));
  }

  // Private helper methods
  private static getEntry<T>(
    key: string,
    config: CacheConfig
  ): CacheEntry<T> | null {
    switch (config.storage) {
      case "memory":
        return this.memoryCache.get(key) || null;
      case "sessionStorage":
      case "localStorage":
        return this.getStorageEntry<T>(key, config.storage);
      default:
        return null;
    }
  }

  private static setMemoryEntry<T>(
    key: string,
    entry: CacheEntry<T>,
    config: CacheConfig
  ): void {
    // Implement LRU eviction if maxSize is reached
    if (config.maxSize && this.memoryCache.size >= config.maxSize) {
      const oldestKey = this.memoryCache.keys().next().value;
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
      }
    }
    this.memoryCache.set(key, entry);
  }

  private static setStorageEntry<T>(
    key: string,
    entry: CacheEntry<T>,
    storage: "sessionStorage" | "localStorage"
  ): void {
    if (typeof window === "undefined") return;

    try {
      const storageObj = window[storage];
      storageObj.setItem(`cache_${key}`, JSON.stringify(entry));
    } catch (error) {
      console.warn(`Failed to set ${storage} entry:`, error);
    }
  }

  private static getStorageEntry<T>(
    key: string,
    storage: "sessionStorage" | "localStorage"
  ): CacheEntry<T> | null {
    if (typeof window === "undefined") return null;

    try {
      const storageObj = window[storage];
      const item = storageObj.getItem(`cache_${key}`);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn(`Failed to get ${storage} entry:`, error);
      return null;
    }
  }

  private static deleteStorageEntry(
    key: string,
    storage: "sessionStorage" | "localStorage"
  ): void {
    if (typeof window === "undefined") return;

    try {
      const storageObj = window[storage];
      storageObj.removeItem(`cache_${key}`);
    } catch (error) {
      console.warn(`Failed to delete ${storage} entry:`, error);
    }
  }

  private static clearStorage(
    storage: "sessionStorage" | "localStorage",
    pattern?: string
  ): void {
    if (typeof window === "undefined") return;

    try {
      const storageObj = window[storage];
      const keysToDelete: string[] = [];

      for (let i = 0; i < storageObj.length; i++) {
        const key = storageObj.key(i);
        if (key?.startsWith("cache_")) {
          if (!pattern || key.includes(pattern)) {
            keysToDelete.push(key);
          }
        }
      }

      keysToDelete.forEach(key => storageObj.removeItem(key));
    } catch (error) {
      console.warn(`Failed to clear ${storage}:`, error);
    }
  }

  private static getStorageSize(
    storage: "sessionStorage" | "localStorage"
  ): number {
    if (typeof window === "undefined") return 0;

    try {
      const storageObj = window[storage];
      let count = 0;
      for (let i = 0; i < storageObj.length; i++) {
        const key = storageObj.key(i);
        if (key?.startsWith("cache_")) {
          count++;
        }
      }
      return count;
    } catch (error) {
      return 0;
    }
  }

  private static cleanupExpiredEntries(): void {
    const now = Date.now();

    // Cleanup memory cache (we don't have TTL per entry, so this is basic)
    // In a real implementation, we'd store TTL with each entry

    // Cleanup storage caches
    this.cleanupStorageEntries("sessionStorage", now);
    this.cleanupStorageEntries("localStorage", now);
  }

  private static cleanupStorageEntries(
    storage: "sessionStorage" | "localStorage",
    now: number
  ): void {
    if (typeof window === "undefined") return;

    try {
      const storageObj = window[storage];
      const keysToDelete: string[] = [];

      for (let i = 0; i < storageObj.length; i++) {
        const key = storageObj.key(i);
        if (key?.startsWith("cache_")) {
          try {
            const item = storageObj.getItem(key);
            if (item) {
              const entry = JSON.parse(item);
              // Note: We'd need to store TTL with each entry for proper cleanup
              // This is a simplified version
            }
          } catch (error) {
            keysToDelete.push(key); // Remove corrupted entries
          }
        }
      }

      keysToDelete.forEach(key => storageObj.removeItem(key));
    } catch (error) {
      console.warn(`Failed to cleanup ${storage}:`, error);
    }
  }

  private static cleanupExpiredPromises(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    Array.from(this.loadingPromises.entries()).forEach(([key, entry]) => {
      if (now - entry.timestamp > this.PROMISE_TTL) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => this.loadingPromises.delete(key));
  }
}

// Initialize on module load
UnifiedCacheManager.initialize();

export { UnifiedCacheManager, type CacheConfig };
